import { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../api/client';
import { orderApi, customerApi, vendorApi, dispatchApi, commentApi, trackingApi, adminAddressApi } from '../api/services';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import LifecycleBadge from '../components/common/LifecycleBadge';
import ValidationFlagsPanel from '../components/orders/ValidationFlagsPanel';
import HandoffModal from '../components/modals/HandoffModal';
import {
  SubstituteProductModal, ClarifyVirtualProductModal, LinkReshipModal,
  SendBackModal, RevisedTrackingModal,
  PartialShipmentModal, RecordItemRefundModal,
} from '../components/modals/OrderActionModals';
import SplitShipmentModal from '../components/modals/SplitShipmentModal';
import EditSplitModal from '../components/modals/EditSplitModal';
import {
  RequestProductChangeModal, AddEditItemModal, CompleteProductChangeModal,
} from '../components/modals/ProductChangeModals';
import {
  EscalateToCCModal, CustomerRespondedModal,
  RequestCancellationModal, ConfirmCancellationModal, FailCancellationModal,
  CloseCancellationModal, CustomerPaidAfterDeliveryModal, MarkTroublesomeModal,
  UpdateRefundStatusModal,
  RequestHoldModal, ReleaseHoldModal, MarkReminderSentModal,
  RecordVendorResponseModal,
  CustomerApprovedAltModal, CustomerRejectedAltModal,
  ShipPartialHoldOosModal, ShipPartialCancelOosModal,
  PresentMultiAltModal, ReshipReassignVendorModal,
  MarkDeliveredModal,
  AddParcelModal,
} from '../components/modals/OpsActionModals';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, Send, Check, RotateCcw, Shuffle, HelpCircle, Link2,
  ArrowRight, AlertCircle, ShieldAlert, Loader2,
  MessageSquare, UserCheck, XOctagon, CheckCircle2, XCircle,
  DollarSign, Pause, Play, Mail, TriangleAlert, Truck, Bell, Package,
  Calendar, RefreshCw, MapPin, Plus, Pencil, Trash2, PackagePlus,
} from 'lucide-react';

const CARRIER_STATUS_COLORS = {
  booked: 'bg-gray-100 text-gray-700',
  in_transit: 'bg-blue-100 text-blue-700',
  customs: 'bg-yellow-100 text-yellow-700',
  out_for_delivery: 'bg-orange-100 text-orange-700',
  delivered: 'bg-green-100 text-green-700',
  returned: 'bg-red-100 text-red-700',
  undelivered: 'bg-red-100 text-red-700',
  not_found: 'bg-gray-100 text-gray-500',
  unknown: 'bg-gray-100 text-gray-500',
};

function CarrierStatusBadge({ status }) {
  const colors = CARRIER_STATUS_COLORS[status] || CARRIER_STATUS_COLORS.unknown;
  const label = (status || 'unknown').replace(/_/g, ' ');
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium capitalize ${colors}`}>
      {label}
    </span>
  );
}

function KV({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className="text-sm text-gray-900 mt-0.5">{children || '—'}</div>
    </div>
  );
}

function Button({ onClick, icon: Icon, children, variant = 'default', disabled = false }) {
  const styles = {
    default: 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50',
    primary: 'bg-blue-600 text-white hover:bg-blue-700',
    warn:    'bg-amber-600 text-white hover:bg-amber-700',
    danger:  'bg-red-600 text-white hover:bg-red-700',
    success: 'bg-emerald-600 text-white hover:bg-emerald-700',
  }[variant];
  return (
    <button disabled={disabled} onClick={onClick}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition ${styles} disabled:opacity-50`}>
      {Icon && <Icon className="w-3.5 h-3.5" />}
      {children}
    </button>
  );
}

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin, isOps, isDispatchUser, isSuperAdmin } = useAuth();

  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyFilter, setHistoryFilter] = useState('all');
  const [vendors, setVendors] = useState([]);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [postingComment, setPostingComment] = useState(false);
  const [loading, setLoading] = useState(true);

  const [handoffOpen, setHandoffOpen] = useState(false);
  const [sendBackOpen, setSendBackOpen] = useState(false);
  const [subItem, setSubItem] = useState(null);
  const [clarifyItem, setClarifyItem] = useState(null);
  const [linkReshipOpen, setLinkReshipOpen] = useState(false);
  const [reviseShipment, setReviseShipment] = useState(null);
  const [markDeliveredShip, setMarkDeliveredShip] = useState(null);
  const [addParcelOpen, setAddParcelOpen] = useState(false);
  const [splitShipment, setSplitShipment] = useState(null);
  const [editSplitOpen, setEditSplitOpen] = useState(false);
  const [deferOpen, setDeferOpen] = useState(false);
  const [deferDate, setDeferDate] = useState('');
  // Ops / cancellation / hold / vendor-response modal states
  const [escalateCCOpen, setEscalateCCOpen] = useState(false);
  const [customerRespondedOpen, setCustomerRespondedOpen] = useState(false);
  const [requestCancelOpen, setRequestCancelOpen] = useState(false);
  const [requestCancelByUserOpen, setRequestCancelByUserOpen] = useState(false);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [failCancelOpen, setFailCancelOpen] = useState(false);
  const [closeCancelOpen, setCloseCancelOpen] = useState(false);
  const [customerPaidOpen, setCustomerPaidOpen] = useState(false);
  const [markTroublesomeOpen, setMarkTroublesomeOpen] = useState(false);
  const [refundStatusOpen, setRefundStatusOpen] = useState(false);
  const [requestHoldOpen, setRequestHoldOpen] = useState(false);
  const [releaseHoldOpen, setReleaseHoldOpen] = useState(false);
  const [reminderSentOpen, setReminderSentOpen] = useState(false);
  const [recordVROpen, setRecordVROpen] = useState(false);
  const [approveAltOpen, setApproveAltOpen] = useState(false);
  const [rejectAltOpen, setRejectAltOpen] = useState(false);
  const [shipHoldOpen, setShipHoldOpen] = useState(false);
  const [shipCancelOpen, setShipCancelOpen] = useState(false);
  const [multiAltOpen, setMultiAltOpen] = useState(false);
  const [reshipReassignOpen, setReshipReassignOpen] = useState(false);
  const [clearTroublesomeOpen, setClearTroublesomeOpen] = useState(false);
  const [clearReason, setClearReason] = useState('');
  const [clearingTroublesome, setClearingTroublesome] = useState(false);
  const [partialShipOpen, setPartialShipOpen] = useState(false);
  const [refundItem, setRefundItem] = useState(null);
  // Ops product-change flow (issue #1)
  const [requestChangeOpen, setRequestChangeOpen] = useState(false);
  const [completeChangeOpen, setCompleteChangeOpen] = useState(false);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    // DMS-20260515: render the page as soon as we have the order. Secondary
    // endpoints (items / timeline / shipments / comments / history) populate
    // in the background — a hung secondary call must not block the spinner.
    try {
      const o = await orderApi.get(id);
      setOrder(o.data);
    } catch (err) {
      if (err.response?.status === 404) toast.error('Order not found');
      else toast.error(err.response?.data?.error || 'Failed to load order');
      setLoading(false);
      return;
    }
    setLoading(false);

    // Fire-and-forget secondary fetches; each settles its own state slice.
    // 10s timeout so a stuck call doesn't leave a section blank forever.
    const cfg = { timeout: 10000 };
    api.get(`/orders/${id}/items`, cfg).then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
    api.get(`/orders/${id}/timeline`, cfg).then((r) => setTimeline(r.data.rows || [])).catch(() => setTimeline([]));
    api.get(`/orders/${id}/shipments`, cfg).then((r) => setShipments(r.data.shipments || [])).catch(() => setShipments([]));
    api.get(`/comments/order/${id}`, cfg)
      .then((r) => setComments(Array.isArray(r.data) ? r.data : (r.data?.comments || [])))
      .catch(() => setComments([]));
    api.get(`/orders/${id}/history`, cfg).then((r) => setHistory(r.data?.events || [])).catch(() => setHistory([]));
  }, [id]);

  async function postComment() {
    if (!commentText.trim()) return;
    setPostingComment(true);
    try {
      // super_admin posts are tagged as owner_note so Kalpana & Ops see
      // them with a distinct chip in the comment timeline. Everyone else
      // posts as a plain general comment.
      const payload = {
        orderId: id,
        content: commentText.trim(),
        ...(isSuperAdmin() ? { commentType: 'owner_note' } : {}),
      };
      await commentApi.create(payload);
      setCommentText('');
      const c = await commentApi.byOrder(id);
      setComments(Array.isArray(c.data) ? c.data : (c.data.comments || []));
      if (isSuperAdmin()) toast.success('Owner comment posted');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to post comment');
    } finally { setPostingComment(false); }
  }
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    vendorApi.list().then((res) => {
      setVendors(res.data.vendors || res.data || []);
    }).catch(() => { /* ignore */ });
  }, []);

  async function submitForReview() {
    try { await orderApi.submitForReview(id, {}); toast.success('Submitted for review'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  }
  async function approveForDispatch() {
    try { await orderApi.approveForDispatch(id); toast.success('Approved'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  }
  async function approveBlocked() {
    try { await customerApi.approveBlockedOrder(id); toast.success('Unblocked'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  }
  async function deferOrder() {
    if (!deferDate) { toast.error('Pick a date'); return; }
    try {
      const res = await orderApi.deferOrder(id, { scheduledDate: deferDate });
      toast.success(`Deferred ${res.data.updated} shipment(s) to ${deferDate}`);
      setDeferOpen(false);
      setDeferDate('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Defer failed');
    }
  }
  async function rescheduleShipment(shipmentId, newDate) {
    try {
      await orderApi.rescheduleShipment(shipmentId, { scheduledDate: newDate });
      toast.success('Rescheduled');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Reschedule failed');
    }
  }
  async function clearTroublesome() {
    if (!clearReason.trim()) { toast.error('Reason required'); return; }
    if (!order?.customer_id) { toast.error('No customer linked to this order'); return; }
    setClearingTroublesome(true);
    try {
      await customerApi.clearTroublesome(order.customer_id, { reason: clearReason.trim() });
      toast.success('Troublesome flag cleared');
      setClearReason('');
      setClearTroublesomeOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally { setClearingTroublesome(false); }
  }

  if (loading) return (
    <div className="p-6 text-sm text-gray-500">
      <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading order…
    </div>
  );
  if (!order) return <div className="p-6 text-sm text-gray-500">Order not found.</div>;

  const stage = order.lifecycle_stage;
  const reason = order.current_owner_reason_code;
  const owner = order.current_owner_team;
  const opsOwned = owner === 'ops';
  const adminOwned = owner === 'dispatch_admin';
  const withVendor = owner === 'vendor';
  const inCancelFlow = ['cancellation_requested', 'cancellation_requested_by_user_vendor_cannot_fulfil',
                        'cancellation_confirmed', 'cancellation_failed'].includes(reason);

  // Submit-for-review visibility — the order is in_review and owned by the
  // dispatch_user team. Two ways to qualify:
  //   1. The viewer is the named current owner (legacy single-assignee flow).
  //   2. The viewer is in the intake batch's assignee list and the order has
  //      no specific user owner (multi-user assignment, UAT round 3).
  // Once anyone submits, the order moves to dispatch_admin and the button
  // disappears for everyone.
  const isAssigneeOfBatch = Array.isArray(order.intake_batch_assignee_ids)
    && order.intake_batch_assignee_ids.includes(user?.id);
  const dispatchUserOwned = order.current_owner_team === 'dispatch_user';
  const canSubmit = isDispatchUser() && stage === 'in_review' && dispatchUserOwned
    && (order.current_owner_user_id === user?.id
        || (order.current_owner_user_id == null && isAssigneeOfBatch));
  const canApprove = isAdmin() && stage === 'in_review' && adminOwned;
  const canSendBack = isAdmin() && stage === 'in_review' && adminOwned;
  const canLinkReship = isAdmin()
    && order.is_reship
    && !order.original_order_id
    && !order.original_order_id_external;
  const canApproveBlocked = isAdmin() && stage === 'blocked_troublesome_customer';

  // H1 (UAT round 3): user/admin can ping Ops + Admin about an on-hold or
  // stuck order. No state change — just an in-app notification. Available
  // when the order is on_hold and the viewer is dispatch_user or admin.
  const canPingReminder = (isDispatchUser() || isAdmin()) && stage === 'on_hold';

  // Ops capabilities — ops role required. Some actions need Ops to be the
  // current owner (responding in a customer-contact flow); others (request
  // hold / cancel) can be initiated on any active order per Kalpana's ask.
  const canOpsAct = isOps() && opsOwned;
  const canEscalateCC = canOpsAct && (!order.ops_sub_queue || order.ops_sub_queue === 'email' || order.ops_sub_queue === 'none');
  const canRecordCustomerResponse = canOpsAct;
  // Ops can request hold / cancel on any active order the customer asks about,
  // not just ones already in the Ops queue. Handoff transitions widened.
  const isOpsRequestable = isOps() && !['completed', 'cancelled', 'delivered_unpaid'].includes(stage);
  const canRequestCancelByOps = isOpsRequestable && !inCancelFlow;
  const canRequestHold = isOpsRequestable && stage !== 'on_hold';
  const canCloseCancel = canOpsAct && reason === 'cancellation_confirmed';
  const canCustomerPaid = canOpsAct && reason === 'cancellation_failed';
  const canMarkTroublesome = canOpsAct && reason === 'cancellation_failed';
  const canUpdateRefundStatus = canOpsAct && (inCancelFlow || stage === 'cancelled' || stage === 'delivered_unpaid');
  // Admin can act on behalf of Ops for the alt-approval flow (Kalpana 2026-05-21
  // — she's dispatch_admin-only but covers Ops work too). Ops still sees it
  // when the order is in their queue.
  const canApproveRejectAlt = opsOwned && reason === 'vendor_oos_need_alt'
    && (isOps() || isAdmin());
  const canPresentMultiAlt = canOpsAct && reason === 'vendor_multi_alt_customer_choice';
  // Only customer holds (hold_started_at set) get the day-15 reminder — the Ops
  // product-change flow also parks orders at on_hold but must NOT show it
  // (mirrors the hold.service markReminderSent guard).
  const canMarkReminderSent = canOpsAct && stage === 'on_hold'
    && order.hold_started_at && !order.hold_reminder_sent_at;

  // Ops product-change flow (issue #1). User/Admin who own the order in review
  // can pause + hand it to Ops. Ops (on_hold + ops-owned, via can_edit_items)
  // add/modify products and return it.
  const canRequestProductChange = (isDispatchUser() || isAdmin())
    && (dispatchUserOwned || adminOwned)
    && ['in_review', 'ready_for_dispatch'].includes(stage);
  const canEditItems = isOps() && !!order.can_edit_items;

  // Dispatch admin capabilities
  const canConfirmCancel = isAdmin() && adminOwned &&
    (reason === 'cancellation_requested' || reason === 'cancellation_requested_by_user_vendor_cannot_fulfil');
  const canFailCancel = canConfirmCancel;
  const canReleaseHold = isAdmin() && adminOwned && stage === 'on_hold';
  const canShipPartial = isAdmin() && adminOwned && reason === 'vendor_alt_rejected';

  // Dispatch user capabilities
  const canRequestCancelByUser = isDispatchUser() &&
    order.current_owner_user_id === user?.id && withVendor;
  // Vendor can call back with an issue (banned product, short expiry, etc.)
  // even AFTER tracking is recorded and the order has moved to admin / ops.
  // Allow recording the response any time the order is still active
  // (not cancelled / completed / delivered_unpaid). The backend's
  // handoff_transitions table already supports the post-TN paths
  // (dispatch_admin → ops with vendor_oos_need_alt et al.).
  //
  // Ops also sees the button when the order is in their queue at
  // vendor_oos_need_alt — that's when Ops/Admin need to add the
  // alternative product details the customer agreed to.
  const isActive = !['cancelled', 'completed', 'delivered_unpaid'].includes(stage);
  const canRecordVendorResponse =
    isActive && (
      isDispatchUser() || isAdmin() ||
      (isOps() && opsOwned && reason === 'vendor_oos_need_alt')
    );
  const canReshipReassign = (isDispatchUser() || isAdmin()) && withVendor && order.is_reship;
  // Partial shipment recording (Kalpana 2026-05-26 + reship fix 2026-05-28):
  // post-TN, multi-item only. The semantic intent is "the parcel has been
  // packed and the vendor told us they short-shipped" — so gate on
  // shipment-presence (any TN-bearing shipment), not on order lifecycle.
  // Reship orders sometimes sit at lifecycle=with_vendor even after the
  // shipment has its TN (the tracking_received auto-promotion doesn't
  // always fire for them — see IDM260513040W).
  const nonCancelledItems = items.filter((it) => !it.cancelled_at);
  const hasShipmentWithTn = shipments.some((s) => s.tracking_number);
  const partialBlockedStages = ['completed', 'cancelled', 'delivered_unpaid', 'partially_shipped', 'on_hold'];
  const canRecordPartialShipment =
    (isAdmin() || isDispatchUser())
    && hasShipmentWithTn
    && nonCancelledItems.length >= 2
    && !partialBlockedStages.includes(stage);
  // Defer: any pending shipment on the order can still be rescheduled
  const pendingShipments = shipments.filter((s) => s.status === 'pending' && !s.dispatch_batch_id);
  const canDefer = (isAdmin() || isDispatchUser()) && pendingShipments.length > 0;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Back target: prefer the batch we came from, fall back to intake list */}
      <Link
        to={order.intake_batch_id ? `/intake/${order.intake_batch_id}` : '/intake'}
        className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline mb-2"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </Link>

      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
        <div className="flex items-start justify-between mb-3 gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-semibold text-gray-900 font-mono">{order.order_id_external}</h1>
              {order.total_parts > 1 && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 font-mono">
                  Part {order.part_number} of {order.total_parts}
                </span>
              )}
              <LifecycleBadge
                stage={order.lifecycle_stage}
                ownerTeam={order.current_owner_team}
                hasReship={order.has_reship}
              />
              {/* Issue #23 — the list view shows only "Reshipped", but here Ops
                  must still see whether the original parcel is moving,
                  delivered or returned, or a genuinely stuck original becomes
                  impossible to chase. */}
              {order.has_reship && (
                <LifecycleBadge stage={order.lifecycle_stage} ownerTeam={order.current_owner_team} />
              )}
              {order.both_delivered_pending_confirm && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Both original and reship delivered — please confirm
                </span>
              )}
              {order.is_reship && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  reship
                  {(isAdmin() || isDispatchUser()) ? (
                    <select
                      value={order.reship_reason || ''}
                      onChange={async (e) => {
                        if (!e.target.value) return;
                        try {
                          await orderApi.setReshipReason(order.id, e.target.value);
                          toast.success('Reship reason saved — billing % updates in Vendor Payables');
                          load();
                        } catch (err) {
                          toast.error(err.response?.data?.error || 'Failed to save reason');
                        }
                      }}
                      title="Why is this being reshipped? Drives what % the vendor charges us."
                      className={`bg-transparent border-0 text-xs font-medium focus:outline-none cursor-pointer ${order.reship_reason ? 'text-purple-800' : 'text-rose-700'}`}>
                      <option value="">⚠ set reason (bills 100%)</option>
                      <option value="customs_hold">stuck in customs (50%)</option>
                      <option value="lost">lost in transit (50%)</option>
                      <option value="damaged">damaged (50%)</option>
                      <option value="missing_pills">missing pills (50%)</option>
                      <option value="wrong_product">wrong product (vendor decides)</option>
                      <option value="customer_feedback">customer dissatisfied (100%)</option>
                      <option value="delivered_want_another">delivered — wants another (100%)</option>
                      <option value="delivered_not_received">delivered, customer says not received (100%)</option>
                      <option value="other">other (100%)</option>
                    </select>
                  ) : (
                    order.reship_reason && <span>· {order.reship_reason.replace(/_/g, ' ')}</span>
                  )}
                </span>
              )}
            </div>
            <div className="text-sm text-gray-600 mt-1">
              Owner: <strong>{order.current_owner_team}</strong>
              {order.current_owner_reason_label && <> · {order.current_owner_reason_label}</>}
              {order.current_owner_since && (
                <> · since {format(new Date(order.current_owner_since), 'dd MMM HH:mm')}</>
              )}
            </div>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            {canSubmit &&        <Button onClick={submitForReview} icon={Send} variant="primary">Submit for review</Button>}
            {canApprove &&       <Button onClick={approveForDispatch} icon={Check} variant="success">Approve</Button>}
            {canSendBack &&      <Button onClick={() => setSendBackOpen(true)} icon={RotateCcw} variant="warn">Send back</Button>}
            {canLinkReship &&    <Button onClick={() => setLinkReshipOpen(true)} icon={Link2} variant="primary">Link original</Button>}
            {canApproveBlocked && <Button onClick={approveBlocked} icon={ShieldAlert} variant="danger">Approve (troublesome)</Button>}
            {canPingReminder && (
              <Button onClick={async () => {
                try {
                  const note = window.prompt('Optional note (or leave empty):', '');
                  if (note === null) return;
                  await orderApi.remind(id, note || null);
                  toast.success('Reminder sent to Admin and Ops');
                } catch (err) { toast.error(err.response?.data?.error || 'Failed to send reminder'); }
              }} icon={Bell} variant="warn">Send reminder</Button>
            )}

            {/* Ops actions */}
            {canRecordCustomerResponse && <Button onClick={() => setCustomerRespondedOpen(true)} icon={MessageSquare} variant="primary">Customer responded</Button>}
            {canEscalateCC &&           <Button onClick={() => setEscalateCCOpen(true)} icon={Mail} variant="warn">Escalate to CC</Button>}
            {canRequestCancelByOps &&   <Button onClick={() => setRequestCancelOpen(true)} icon={XOctagon} variant="warn">Request cancel</Button>}
            {canRequestHold &&          <Button onClick={() => setRequestHoldOpen(true)} icon={Pause} variant="warn">Request hold</Button>}
            {canCloseCancel &&          <Button onClick={() => setCloseCancelOpen(true)} icon={CheckCircle2} variant="success">Close cancel</Button>}
            {canCustomerPaid &&         <Button onClick={() => setCustomerPaidOpen(true)} icon={DollarSign} variant="success">Customer paid</Button>}
            {canMarkTroublesome &&      <Button onClick={() => setMarkTroublesomeOpen(true)} icon={TriangleAlert} variant="danger">Mark troublesome</Button>}
            {canUpdateRefundStatus &&   <Button onClick={() => setRefundStatusOpen(true)} icon={DollarSign}>Refund status</Button>}
            {canApproveRejectAlt &&    <>
              <Button onClick={() => setApproveAltOpen(true)} icon={CheckCircle2} variant="success">Approve alt</Button>
              <Button onClick={() => setRejectAltOpen(true)} icon={XCircle} variant="warn">Reject alt</Button>
            </>}
            {canPresentMultiAlt &&      <Button onClick={() => setMultiAltOpen(true)} icon={UserCheck} variant="primary">Send multi-alt</Button>}
            {canMarkReminderSent &&     <Button onClick={() => setReminderSentOpen(true)} icon={Bell} variant="primary">Reminder sent</Button>}

            {/* Dispatch admin actions */}
            {canConfirmCancel &&        <Button onClick={() => setConfirmCancelOpen(true)} icon={Check} variant="success">Confirm cancel</Button>}
            {canFailCancel &&           <Button onClick={() => setFailCancelOpen(true)} icon={XCircle} variant="danger">Mark shipped</Button>}
            {canReleaseHold &&          <Button onClick={() => setReleaseHoldOpen(true)} icon={Play} variant="success">Release hold</Button>}
            {canShipPartial &&          <>
              <Button onClick={() => setShipHoldOpen(true)} icon={Package}>Ship partial + hold</Button>
              <Button onClick={() => setShipCancelOpen(true)} icon={Package}>Ship partial + cancel</Button>
            </>}

            {/* Dispatch user actions */}
            {canRequestCancelByUser &&  <Button onClick={() => setRequestCancelByUserOpen(true)} icon={XOctagon} variant="warn">Cancel — vendor can't fulfil</Button>}
            {canRecordVendorResponse && <Button onClick={() => setRecordVROpen(true)} icon={Truck} variant="warn">Record vendor response</Button>}
            {canReshipReassign &&       <Button onClick={() => setReshipReassignOpen(true)} icon={Shuffle}>Reassign reship vendor</Button>}
            {canDefer &&                 <Button onClick={() => setDeferOpen(true)} icon={Calendar}>Defer order</Button>}
            {canRecordPartialShipment && <Button onClick={() => setPartialShipOpen(true)} icon={Package} variant="warn">Update partial shipment</Button>}

            {/* Ops product-change flow (issue #1) */}
            {canRequestProductChange && <Button onClick={() => setRequestChangeOpen(true)} icon={PackagePlus} variant="warn">Request product change</Button>}
            {canEditItems &&            <Button onClick={() => setCompleteChangeOpen(true)} icon={ArrowRight} variant="success">Return order</Button>}

            <Button onClick={() => setHandoffOpen(true)} icon={ArrowRight}>Hand off</Button>
          </div>
        </div>

        {order.siblings && order.siblings.length > 1 && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold text-blue-900 uppercase tracking-wide">
                Sibling parts of {order.order_id_external}
              </div>
              {order.can_edit_split && (isAdmin() || isDispatchUser()) && (
                <button onClick={() => setEditSplitOpen(true)}
                        className="flex items-center gap-1 text-xs px-2 py-1 bg-white border border-blue-300 text-blue-700 rounded hover:bg-blue-100">
                  <Shuffle className="w-3 h-3" /> Edit split
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {order.siblings.map((sib) => {
                const isCurrent = sib.id === order.id;
                const cls = isCurrent
                  ? 'bg-blue-600 text-white border-blue-700'
                  : 'bg-white text-gray-800 border-gray-300 hover:bg-blue-100';
                const inner = (
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-xs font-mono ${cls}`}>
                    Part {sib.part_number}/{sib.total_parts}
                    <span className="opacity-70">·</span>
                    <span>{sib.lifecycle_stage}</span>
                    {sib.scheduled_date && (
                      <>
                        <span className="opacity-70">·</span>
                        <span>{format(new Date(sib.scheduled_date), 'dd MMM')}</span>
                      </>
                    )}
                  </span>
                );
                return isCurrent
                  ? <span key={sib.id}>{inner}</span>
                  : <Link key={sib.id} to={`/orders/${sib.id}`}>{inner}</Link>;
              })}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KV label="Customer">{order.customer_name}</KV>
          <KV label="Email">{order.customer_email}</KV>
          {isSuperAdmin() && (
            <KV label="Total">
              {order.total_amount != null ? `$${Number(order.total_amount).toFixed(2)}` : '—'}
            </KV>
          )}
          <KV label="Vendor">{order.vendor_id ? order.vendor_id.slice(0, 8) + '…' : '—'}</KV>
          <KV label="Address">
            {order.shipping_address1}{order.shipping_address2 ? `, ${order.shipping_address2}` : ''}
          </KV>
          <KV label="City / State">{order.shipping_city}, {order.shipping_zone}</KV>
          <KV label="Zip">{order.shipping_postcode}</KV>
          <KV label="Country">{order.shipping_country}</KV>
        </div>

        {order.pending_address_correction && (
          <PendingAddressBanner
            order={order}
            isAdmin={isAdmin()}
            onResolved={load}
          />
        )}

        {order.customer_is_troublesome && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start justify-between gap-2">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
              <div className="text-sm text-red-900">
                <strong>Troublesome customer.</strong> {order.customer_troublesome_count} past troublesome order(s).
                {order.lifecycle_stage === 'blocked_troublesome_customer' && ' Order is blocked until approved.'}
              </div>
            </div>
            {(isOps() || (user?.roles || []).includes('super_admin')) && (
              <button onClick={() => setClearTroublesomeOpen(true)}
                      className="text-xs px-2 py-1 bg-white border border-red-300 text-red-700 rounded hover:bg-red-100 shrink-0">
                Clear flag
              </button>
            )}
          </div>
        )}

        {order.data_quality_notes && (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
            <strong>Data quality:</strong> {order.data_quality_notes}
          </div>
        )}
      </div>

      {/* Round-5: structured validation flag panel. Only renders when admin
          has flags to act on; reload the order after resolution so the
          lifecycle badge / data-quality note refresh too. */}
      {(isAdmin()) && (
        <ValidationFlagsPanel orderId={id} onResolved={load} />
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-4">
        <div className="px-5 py-3 border-b border-gray-200 font-medium text-sm flex items-center justify-between">
          <span>Items</span>
          {canEditItems && (
            <button onClick={() => setAddItemOpen(true)}
                    className="flex items-center gap-1 text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700">
              <Plus className="w-3 h-3" /> Add product
            </button>
          )}
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs font-semibold uppercase text-gray-500">
            <tr>
              <th className="text-left px-4 py-2">Product</th>
              <th className="text-left px-4 py-2">Brand</th>
              <th className="text-left px-4 py-2">Manufacturer</th>
              <th className="text-right px-4 py-2">Qty</th>
              {/* Unit cost — super_admin only (Kalpana 2026-04-29 follow-up:
                  the Items table 'Unit' column was leaking unit_cost to
                  every role). */}
              {isSuperAdmin() && <th className="text-right px-4 py-2">Unit</th>}
              <th className="text-left px-4 py-2">Refund</th>
              <th className="text-right px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const isCancelled = !!it.cancelled_at;
              const isRefunded  = !!it.refunded_at;
              const rowCls = `border-t border-gray-100 ${isCancelled ? 'bg-amber-50/30' : ''}`;
              const cancelTooltip = isCancelled
                ? `Not shipped by vendor — ${it.cancelled_reason || 'reason unknown'}`
                : '';
              return (
                <tr key={it.id} className={rowCls}>
                  <td className="px-4 py-2">
                    {it.original_product_name && it.original_product_name !== it.product_name && (
                      <div
                        className="text-xs text-gray-400 line-through"
                        title="Original product — replaced by approved alternative"
                      >
                        {it.original_product_name}
                      </div>
                    )}
                    <div className={isCancelled ? 'line-through text-gray-500' : ''} title={cancelTooltip}>
                      {it.product_name}
                    </div>
                    {it.is_virtual_product && <span className="text-xs text-amber-700">(virtual)</span>}
                    {isCancelled && (
                      <div className="text-xs text-amber-700 mt-0.5">
                        Not shipped — {it.cancelled_reason || 'reason unknown'}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {it.original_indian_brand && it.original_indian_brand !== it.indian_brand && (
                      <div
                        className="text-xs text-gray-400 line-through"
                        title="Original brand — replaced by approved alternative"
                      >
                        {it.original_indian_brand}
                      </div>
                    )}
                    <div className={isCancelled ? 'line-through text-gray-500' : ''}>
                      {it.indian_brand || '—'}
                    </div>
                  </td>
                  <td className={`px-4 py-2 ${isCancelled ? 'line-through text-gray-500' : ''}`}>
                    {it.manufacturer || '—'}
                  </td>
                  <td className={`px-4 py-2 text-right ${isCancelled ? 'line-through text-gray-500' : ''}`}>
                    {it.quantity}
                  </td>
                  {isSuperAdmin() && (
                    <td className={`px-4 py-2 text-right ${isCancelled ? 'line-through text-gray-500' : ''}`}>
                      {it.unit_cost != null ? `$${Number(it.unit_cost).toFixed(2)}` : '—'}
                    </td>
                  )}
                  <td className="px-4 py-2">
                    {!isCancelled ? (
                      <span className="text-xs text-gray-400">—</span>
                    ) : isRefunded ? (
                      <div className="text-xs text-green-700">
                        ₹{Number(it.refund_amount).toFixed(2)}
                        <div className="text-[10px] text-gray-500">
                          {it.refunded_at && format(new Date(it.refunded_at), 'dd MMM HH:mm')}
                        </div>
                      </div>
                    ) : isOps() ? (
                      <Button onClick={() => setRefundItem(it)} icon={DollarSign} variant="primary">
                        Record refund
                      </Button>
                    ) : (
                      <span className="text-xs text-amber-700">Pending</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex justify-end gap-1">
                      {/* Both admin and dispatch_user can substitute per schema §15 */}
                      {(isAdmin() || isDispatchUser()) && stage === 'in_review' && (
                        <Button onClick={() => setSubItem(it)} icon={Shuffle}>Substitute</Button>
                      )}
                      {isOps() && it.is_virtual_product && stage === 'data_incomplete' && (
                        <Button onClick={() => setClarifyItem(it)} icon={HelpCircle} variant="primary">Clarify</Button>
                      )}
                      {/* Ops product-change flow (issue #1): edit any item;
                          remove only Ops-added items (it.added_at set). */}
                      {canEditItems && !isCancelled && (
                        <Button onClick={() => setEditItem(it)} icon={Pencil}>Edit</Button>
                      )}
                      {canEditItems && it.added_at && (
                        <Button
                          onClick={async () => {
                            if (!window.confirm(`Remove "${it.product_name}"? This product was added in this change.`)) return;
                            try {
                              const res = await orderApi.removeItem(it.id);
                              toast.success('Product removed');
                              if (res.data?.revised_file_batches?.length) {
                                toast(`Vendor file for batch ${res.data.revised_file_batches.join(', ')} must be regenerated and re-sent.`, { icon: '⚠️', duration: 8000 });
                              }
                              load();
                            } catch (err) {
                              toast.error(err.response?.data?.error || 'Remove failed');
                            }
                          }}
                          icon={Trash2}
                          variant="danger"
                        >
                          Remove
                        </Button>
                      )}
                      {/* Reverse cancellation — Dispatch Admin only, blocked
                          once a refund has been recorded (D8). */}
                      {isAdmin() && isCancelled && (
                        <Button
                          onClick={async () => {
                            if (isRefunded) return;
                            const note = window.prompt('Optional note for the reversal (or leave blank):', '');
                            if (note === null) return;
                            try {
                              await orderApi.reverseCancellation(it.id, { reasonNote: note || null });
                              toast.success('Cancellation reversed');
                              load();
                            } catch (err) {
                              toast.error(err.response?.data?.error || 'Reverse failed');
                            }
                          }}
                          icon={RotateCcw}
                          variant="warn"
                          disabled={isRefunded}
                          title={isRefunded ? 'Refund already issued — undo the refund first to enable reversal' : ''}
                        >
                          Reverse
                        </Button>
                      )}
                      {/* Undo refund — OPS only, when a refund record exists. */}
                      {isOps() && isRefunded && (
                        <Button
                          onClick={async () => {
                            if (!window.confirm('Undo this refund record? Used when correcting a mistake or before reversing the cancellation.')) return;
                            try {
                              await orderApi.undoItemRefund(it.id, {});
                              toast.success('Refund undone');
                              load();
                            } catch (err) {
                              toast.error(err.response?.data?.error || 'Undo failed');
                            }
                          }}
                          icon={XCircle}
                        >
                          Undo refund
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {shipments.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 mb-4">
          <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between gap-3">
            <span className="font-medium text-sm">Shipments</span>
            {/* #33: the vendor sometimes splits at dispatch and returns a second
                tracking number. Pre-dispatch Split Order can't be used by then,
                so Ops records the extra parcel here. */}
            {(isOps() || isAdmin()) && shipments.some((s) => ['dispatched', 'in_transit'].includes(s.status)) && (
              <Button onClick={() => setAddParcelOpen(true)}>+ Add parcel</Button>
            )}
          </div>
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1000px]">
            <thead className="bg-gray-50 text-xs font-semibold uppercase text-gray-500">
              <tr>
                <th className="text-left px-4 py-2">#</th>
                <th className="text-left px-4 py-2">Product</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Qty</th>
                <th className="text-left px-4 py-2">Scheduled</th>
                <th className="text-left px-4 py-2">Vendor</th>
                <th className="text-left px-4 py-2">Dispatch batch</th>
                <th className="text-left px-4 py-2">Tracking</th>
                <th className="text-left px-4 py-2">Carrier Status</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {shipments.map((s) => {
                const canRevise = isAdmin() && ['completed', 'in_transit', 'delivered_unpaid'].includes(stage);
                // Vendor change/allocation: any pre-dispatch shipment can be
                // re-allocated. Lock once it's on a dispatch batch run or
                // already moving (status != 'pending'). After auto-assign at
                // intake (round 5), this is the path admin uses to switch
                // vendor before dispatch. Once dispatched, vendor changes
                // happen via the vendor-response reassign flow instead.
                const canAllocate = (isAdmin() || isDispatchUser())
                  && s.status === 'pending'
                  && !s.dispatch_batch_id;
                // Split: admin or dispatch_user, only on pending shipments not yet on a batch
                const splittable = (s.items || []).some((it) => it.quantity > 1) || (s.items || []).length > 1;
                const canSplit = (isAdmin() || isDispatchUser()) && s.status === 'pending' && !s.dispatch_batch_id && splittable;
                const canReschedule = (isAdmin() || isDispatchUser()) && s.status === 'pending' && !s.dispatch_batch_id;
                // Mark delivered (issue #11): Ops/Admin, when the carrier site
                // confirms delivery but DMS still shows the parcel stuck. Needs
                // a tracking number and a non-terminal carrier status.
                // No carrier_status requirement: a parcel we cannot track at all
                // (Emirates Post — see #32) never gets one, and those are exactly
                // the ones Ops has to confirm by hand. The server only requires a
                // tracking number and that it isn't already delivered.
                const canMarkDelivered = (isOps() || isAdmin())
                  && s.tracking_number
                  && !['delivered', 'returned'].includes(s.carrier_status);
                return (
                  <tr key={s.id} className="border-t border-gray-100">
                    <td className="px-4 py-2 align-top">{s.shipment_number}</td>
                    <td className="px-4 py-2 align-top">
                      {(s.items || []).length === 0 ? (
                        <div>—</div>
                      ) : (
                        <ul className="space-y-1.5">
                          {(s.items || []).map((it, ix) => (
                            <li key={it.order_item_id || ix} className="flex items-baseline gap-2">
                              <span>{it.product_name || '—'}</span>
                              <span className="text-gray-400">·</span>
                              <span className="font-medium tabular-nums">{it.quantity}</span>
                              {it.indian_brand && (
                                <span className="text-[11px] text-gray-500 ml-1">{it.indian_brand}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td className="px-4 py-2 capitalize align-top">{s.status}</td>
                    <td className="px-4 py-2 text-right align-top tabular-nums font-medium">
                      {s.quantity || 0}
                    </td>
                    <td className="px-4 py-2 align-top">{s.scheduled_date}</td>
                    <td className="px-4 py-2 align-top">
                      {canAllocate ? (
                        <select
                          value={s.vendor_id || ''}
                          onChange={async (e) => {
                            if (!e.target.value) return;
                            if (e.target.value === s.vendor_id) return;
                            try {
                              await dispatchApi.allocate({ shipmentId: s.id, vendorId: e.target.value });
                              toast.success(s.vendor_id ? 'Vendor changed' : 'Vendor allocated');
                              load();
                            } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
                          }}
                          className="text-xs px-2 py-1 border border-gray-300 rounded"
                          title={s.vendor_id ? 'Change vendor' : 'Allocate vendor'}
                        >
                          <option value="" disabled>Allocate vendor…</option>
                          {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                        </select>
                      ) : (
                        s.vendor_name || '—'
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {s.dispatch_batch_id ? (
                        <Link to={`/dispatch/${s.dispatch_batch_id}`} className="text-blue-600 hover:underline">
                          {s.dispatch_batch_number}
                        </Link>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">
                      {s.tracking_number || '—'}
                      {s.revised_tracking && <span className="ml-1 text-[10px] text-amber-700">(revised)</span>}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      {s.carrier_status ? (
                        <div className="flex items-center gap-1">
                          <CarrierStatusBadge status={s.carrier_status} />
                          <button
                            onClick={async () => {
                              try {
                                const { data } = await trackingApi.carrierRefresh(s.id);
                                toast.success(data.tracking?.skipped
                                  ? 'Status is final (delivered/returned) — not re-checked'
                                  : 'Carrier status refreshed');
                                load();
                              } catch (err) { toast.error(err.response?.data?.error || 'Refresh failed'); }
                            }}
                            className="ml-1 p-0.5 text-gray-400 hover:text-blue-600"
                            title="Refresh carrier status"
                          >
                            <RefreshCw className="w-3 h-3" />
                          </button>
                        </div>
                      ) : s.tracking_number ? (
                        <button
                          onClick={async () => {
                            try {
                              await trackingApi.carrierRefresh(s.id);
                              toast.success('Carrier tracking started');
                              load();
                            } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
                          }}
                          className="text-blue-600 hover:underline text-[11px]"
                        >
                          Check status
                        </button>
                      ) : '—'}
                      {s.carrier_last_event && (
                        <div className="text-[10px] text-gray-500 mt-0.5 max-w-[180px] truncate" title={s.carrier_last_event}>
                          {s.carrier_last_event}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex flex-col items-end gap-1">
                        {canReschedule && (
                          <input
                            type="date"
                            defaultValue={s.scheduled_date ? String(s.scheduled_date).slice(0, 10) : ''}
                            onChange={(e) => {
                              if (e.target.value) rescheduleShipment(s.id, e.target.value);
                            }}
                            className="text-xs px-1 py-0.5 border border-gray-300 rounded"
                            title="Change scheduled date"
                          />
                        )}
                        <div className="flex justify-end gap-1 flex-wrap">
                          {canSplit && (
                            <Button onClick={() => setSplitShipment(s)}>Split</Button>
                          )}
                          {canRevise && (
                            <Button onClick={() => setReviseShipment(s)}>Revise TN</Button>
                          )}
                          {canMarkDelivered && (
                            <Button variant="success" onClick={() => setMarkDeliveredShip(s)}>Mark delivered</Button>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Carrier Tracking — India Post tracking timeline per shipment */}
      {shipments.some(s => s.carrier_status || s.carrier_events) && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-4">
          <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
            <div className="font-medium text-sm flex items-center gap-2">
              <Truck className="w-4 h-4 text-blue-600" />
              Carrier Tracking (India Post)
            </div>
            <div className="flex items-center gap-3">
              {shipments.some(s => s.carrier_last_checked_at) && (
                <div className="text-[10px] text-gray-400">
                  Last checked: {format(new Date(shipments.find(s => s.carrier_last_checked_at)?.carrier_last_checked_at), 'dd MMM yyyy HH:mm')}
                </div>
              )}
            </div>
          </div>

          {shipments.filter(s => s.carrier_status || s.carrier_events).map(s => {
            // carrier_events / carrier_article_info come from JSONB columns. The
            // pg driver already parses JSONB into JS objects, so JSON.parse on
            // them throws SyntaxError. (B2 Kalpana UAT round 3 — order detail
            // page failed to load on any completed/in-transit order with
            // India Post events. Repro: order IDM260403058C.) Accept both:
            // pre-parsed object or string.
            const safeJson = (v, fallback) => {
              if (v == null) return fallback;
              if (typeof v === 'string') {
                try { return JSON.parse(v); } catch { return fallback; }
              }
              return v;
            };
            const events = safeJson(s.carrier_events, []);
            const articleInfo = safeJson(s.carrier_article_info, null);

            return (
              <div key={s.id} className="border-b border-gray-100 last:border-b-0">
                {/* Shipment header with status + article info */}
                <div className="px-5 py-3 bg-gray-50 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-xs font-medium text-gray-700">{s.tracking_number}</span>
                    {s.carrier_status && <CarrierStatusBadge status={s.carrier_status} />}
                    {shipments.filter(sh => sh.carrier_events).length > 1 && (
                      <span className="text-[10px] text-gray-400">Shipment #{s.shipment_number}</span>
                    )}
                  </div>
                  {articleInfo && (
                    <div className="flex items-center gap-4 text-[10px] text-gray-500">
                      {articleInfo.origin && articleInfo.destination && (
                        <span>{articleInfo.origin} → {articleInfo.destination}</span>
                      )}
                      {articleInfo.weight && <span>{articleInfo.weight} kg</span>}
                      {articleInfo.value && <span>₹{articleInfo.value}</span>}
                    </div>
                  )}
                </div>

                {/* Event timeline */}
                {events.length > 0 && (
                  <div className="px-5 py-3 max-h-[350px] overflow-y-auto">
                    <ul className="space-y-3">
                      {events.map((ev, i) => {
                        const isFirst = i === 0;
                        const isDelivered = /deliver/i.test(ev.event);
                        const isBooked = /booked/i.test(ev.event);
                        const dotColor = isDelivered ? 'bg-green-500' : isFirst ? 'bg-blue-500' : isBooked ? 'bg-gray-400' : 'bg-gray-300';

                        return (
                          <li key={i} className="relative pl-5">
                            <div className={`absolute left-0 top-1.5 w-2 h-2 rounded-full ${dotColor}`}></div>
                            {i < events.length - 1 && <div className="absolute left-[3px] top-3.5 bottom-[-12px] w-px bg-gray-200" />}
                            <div className="text-sm">
                              <span className={`font-medium ${isFirst ? 'text-gray-900' : 'text-gray-700'}`}>{ev.event}</span>
                              {ev.location && (
                                <span className="text-gray-400 ml-2 text-xs">
                                  <MapPin className="w-3 h-3 inline -mt-0.5 mr-0.5" />
                                  {ev.location}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-400 mt-0.5">{ev.timestamp}</div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <OrderHistorySection
        history={history}
        filter={historyFilter}
        onFilter={setHistoryFilter}
      />

      {/* Comments — threaded discussion on the order (v1 parity) */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mt-4">
        <div className="font-medium text-sm mb-3">Comments</div>
        {comments.length === 0 ? (
          <p className="text-sm text-gray-500 mb-3">No comments yet. Be the first to add one.</p>
        ) : (
          <ul className="space-y-3 mb-4">
            {comments.map((c) => {
              const isOwnerNote = c.comment_type === 'owner_note';
              return (
                <li key={c.id} className={`pl-3 ${isOwnerNote
                    ? 'border-l-4 border-amber-400 bg-amber-50/60 rounded-r-lg py-2 pr-3'
                    : 'border-l-2 border-gray-200'}`}>
                  <div className="flex items-baseline gap-2 text-xs text-gray-500">
                    <span className={`font-semibold ${isOwnerNote ? 'text-amber-900' : 'text-gray-700'}`}>
                      {c.first_name || ''} {c.last_name || ''}
                    </span>
                    {c.user_role && <span className="text-gray-400">· {c.user_role}</span>}
                    {isOwnerNote ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 text-[10px] font-semibold uppercase tracking-wide">
                        Owner
                      </span>
                    ) : c.comment_type && c.comment_type !== 'general' ? (
                      <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[10px]">{c.comment_type}</span>
                    ) : null}
                    {c.created_at && <span className="ml-auto">{format(new Date(c.created_at), 'dd MMM HH:mm')}</span>}
                  </div>
                  <div className={`text-sm mt-0.5 whitespace-pre-wrap ${isOwnerNote ? 'text-amber-950 font-medium' : 'text-gray-800'}`}>{c.content}</div>
                </li>
              );
            })}
          </ul>
        )}
        {isSuperAdmin() && (
          <div className="mb-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block">
            Posting as <strong>Owner</strong> — Kalpana &amp; Ops will see your comment with a highlighted chip.
          </div>
        )}
        <div className="flex gap-2">
          <textarea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder={isSuperAdmin() ? 'Add an owner comment…' : 'Add a comment…'}
            rows={2}
            className={`flex-1 px-3 py-2 border rounded-lg text-sm ${
              isSuperAdmin() ? 'border-amber-300 focus:ring-2 focus:ring-amber-200' : 'border-gray-300'
            }`}
          />
          <button
            onClick={postComment}
            disabled={postingComment || !commentText.trim()}
            className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg text-sm font-medium disabled:opacity-50 h-fit ${
              isSuperAdmin() ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {postingComment && <Loader2 className="w-4 h-4 animate-spin" />} {isSuperAdmin() ? 'Post as Owner' : 'Post'}
          </button>
        </div>
      </div>

      <HandoffModal
        isOpen={handoffOpen} onClose={() => setHandoffOpen(false)} onDone={load}
        orderId={id} fromTeam={order.current_owner_team}
      />
      <SendBackModal isOpen={sendBackOpen} onClose={() => setSendBackOpen(false)} onDone={load} order={order} />
      {subItem && <SubstituteProductModal
        isOpen={!!subItem} onClose={() => setSubItem(null)} onDone={load} item={subItem} />}
      {clarifyItem && <ClarifyVirtualProductModal
        isOpen={!!clarifyItem} onClose={() => setClarifyItem(null)} onDone={load} order={order} item={clarifyItem} />}
      <LinkReshipModal
        isOpen={linkReshipOpen} onClose={() => setLinkReshipOpen(false)} onDone={load}
        order={order} vendors={vendors} />
      {reviseShipment && <RevisedTrackingModal
        isOpen={!!reviseShipment} onClose={() => setReviseShipment(null)} onDone={load} shipment={reviseShipment} />}
      {addParcelOpen && <AddParcelModal
        isOpen={addParcelOpen} onClose={() => setAddParcelOpen(false)} onDone={load} order={order} />}
      {markDeliveredShip && <MarkDeliveredModal
        isOpen={!!markDeliveredShip} onClose={() => setMarkDeliveredShip(null)} onDone={load} shipment={markDeliveredShip} />}
      {splitShipment && <SplitShipmentModal
        isOpen={!!splitShipment} onClose={() => setSplitShipment(null)} onDone={load} shipment={splitShipment} />}
      {editSplitOpen && <EditSplitModal
        isOpen={editSplitOpen} onClose={() => setEditSplitOpen(false)} orderId={id}
        onDone={(result) => {
          // The part being viewed may have been removed by the edit — jump
          // to the first surviving part in that case.
          const stillExists = result?.parts?.some((p) => p.orderId === id);
          if (!stillExists && result?.parts?.length) navigate(`/orders/${result.parts[0].orderId}`);
          else load();
        }} />}

      {/* Defer whole order — sets scheduled_date on every pending shipment */}
      {deferOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setDeferOpen(false)}>
          <div className="bg-white rounded-xl max-w-md w-full mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-gray-200 text-sm font-semibold">Defer order</div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-xs text-gray-600">
                Move every pending shipment on this order to a single new date. Already-dispatched
                shipments are untouched. Affects <strong>{pendingShipments.length}</strong> shipment(s).
              </p>
              {/* Preset buttons — Kalpana asked for +3/+7/+14 quick picks alongside a custom picker */}
              <div>
                <div className="text-xs text-gray-600 mb-1">Quick picks</div>
                <div className="flex gap-2">
                  {[3, 7, 14].map((days) => {
                    const d = new Date();
                    d.setDate(d.getDate() + days);
                    const iso = d.toISOString().slice(0, 10);
                    return (
                      <button key={days}
                              onClick={() => setDeferDate(iso)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
                                deferDate === iso
                                  ? 'bg-blue-600 border-blue-600 text-white'
                                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}>
                        +{days} days
                      </button>
                    );
                  })}
                </div>
              </div>
              <label className="block">
                <span className="block text-xs text-gray-600 mb-1">Or pick a custom date</span>
                <input type="date" value={deferDate} onChange={(e) => setDeferDate(e.target.value)}
                       className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </label>
              <div className="flex justify-end gap-2">
                <button onClick={() => setDeferOpen(false)}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button onClick={deferOrder}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
                  Defer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ops / cancellation / hold / vendor-response modals */}
      <EscalateToCCModal isOpen={escalateCCOpen} onClose={() => setEscalateCCOpen(false)} onDone={load} order={order} />
      <CustomerRespondedModal isOpen={customerRespondedOpen} onClose={() => setCustomerRespondedOpen(false)} onDone={load} order={order} />
      <RequestCancellationModal isOpen={requestCancelOpen} onClose={() => setRequestCancelOpen(false)} onDone={load} order={order} />
      <RequestCancellationModal isOpen={requestCancelByUserOpen} onClose={() => setRequestCancelByUserOpen(false)} onDone={load} order={order} byDispatchUser />
      <ConfirmCancellationModal isOpen={confirmCancelOpen} onClose={() => setConfirmCancelOpen(false)} onDone={load} order={order} />
      <FailCancellationModal isOpen={failCancelOpen} onClose={() => setFailCancelOpen(false)} onDone={load} order={order} />
      <CloseCancellationModal isOpen={closeCancelOpen} onClose={() => setCloseCancelOpen(false)} onDone={load} order={order} />
      <CustomerPaidAfterDeliveryModal isOpen={customerPaidOpen} onClose={() => setCustomerPaidOpen(false)} onDone={load} order={order} />
      <MarkTroublesomeModal isOpen={markTroublesomeOpen} onClose={() => setMarkTroublesomeOpen(false)} onDone={load} order={order} />
      <UpdateRefundStatusModal isOpen={refundStatusOpen} onClose={() => setRefundStatusOpen(false)} onDone={load} order={order} />
      <RequestHoldModal isOpen={requestHoldOpen} onClose={() => setRequestHoldOpen(false)} onDone={load} order={order} />
      <ReleaseHoldModal isOpen={releaseHoldOpen} onClose={() => setReleaseHoldOpen(false)} onDone={load} order={order}
        onRequestLink={() => setLinkReshipOpen(true)} />
      <MarkReminderSentModal isOpen={reminderSentOpen} onClose={() => setReminderSentOpen(false)} onDone={load} order={order} />
      <RecordVendorResponseModal isOpen={recordVROpen} onClose={() => setRecordVROpen(false)} onDone={load} order={order} items={items} />
      {partialShipOpen && <PartialShipmentModal isOpen={partialShipOpen} onClose={() => setPartialShipOpen(false)} onDone={load} order={order} items={items.filter((it) => !it.cancelled_at)} />}
      {refundItem && <RecordItemRefundModal isOpen={!!refundItem} onClose={() => setRefundItem(null)} onDone={load} item={refundItem} />}
      {/* Ops product-change flow (issue #1) */}
      <RequestProductChangeModal isOpen={requestChangeOpen} onClose={() => setRequestChangeOpen(false)} onDone={load} order={order} />
      <CompleteProductChangeModal isOpen={completeChangeOpen} onClose={() => setCompleteChangeOpen(false)} onDone={load} order={order} />
      {addItemOpen && <AddEditItemModal isOpen={addItemOpen} mode="add" onClose={() => setAddItemOpen(false)} onDone={load} order={order} />}
      {editItem && <AddEditItemModal isOpen={!!editItem} mode="edit" onClose={() => setEditItem(null)} onDone={load} item={editItem} />}
      <CustomerApprovedAltModal isOpen={approveAltOpen} onClose={() => setApproveAltOpen(false)} onDone={load} order={order} />
      <CustomerRejectedAltModal isOpen={rejectAltOpen} onClose={() => setRejectAltOpen(false)} onDone={load} order={order} />
      <ShipPartialHoldOosModal isOpen={shipHoldOpen} onClose={() => setShipHoldOpen(false)} onDone={load} order={order} />
      <ShipPartialCancelOosModal isOpen={shipCancelOpen} onClose={() => setShipCancelOpen(false)} onDone={load} order={order} />
      <PresentMultiAltModal isOpen={multiAltOpen} onClose={() => setMultiAltOpen(false)} onDone={load} order={order} />
      <ReshipReassignVendorModal isOpen={reshipReassignOpen} onClose={() => setReshipReassignOpen(false)} onDone={load} order={order} vendors={vendors} />

      {/* Clear troublesome flag — Ops / Super Admin only (R35) */}
      {clearTroublesomeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setClearTroublesomeOpen(false)}>
          <div className="bg-white rounded-xl max-w-md w-full mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-gray-200 text-sm font-semibold">Clear troublesome flag</div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-xs text-gray-600">
                This clears the flag across ALL future orders from this customer. Reason is recorded in audit.
              </p>
              <label className="block">
                <span className="block text-xs text-gray-600 mb-1">Reason *</span>
                <textarea value={clearReason} onChange={(e) => setClearReason(e.target.value)} rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </label>
              <div className="flex justify-end gap-2">
                <button onClick={() => setClearTroublesomeOpen(false)}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button onClick={clearTroublesome} disabled={clearingTroublesome}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50">
                  {clearingTroublesome && <Loader2 className="w-4 h-4 animate-spin" />} Clear flag
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- OrderHistorySection — unified timeline w/ filter chips ---------------
function OrderHistorySection({ history, filter, onFilter }) {
  const counts = history.reduce((m, e) => {
    m.all = (m.all || 0) + 1;
    m[e.category] = (m[e.category] || 0) + 1;
    return m;
  }, {});
  const chips = [
    { key: 'all',          label: 'All' },
    { key: 'substitution', label: 'Substitutions' },
    { key: 'vendor',       label: 'Vendor changes' },
    { key: 'customer',     label: 'Customer interactions' },
    { key: 'comment',      label: 'Comments' },
    { key: 'system',       label: 'System events' },
  ];
  const filtered = filter === 'all' ? history : history.filter((e) => e.category === filter);
  const dotByCat = {
    substitution: 'bg-purple-500',
    vendor:       'bg-amber-500',
    customer:     'bg-blue-500',
    comment:      'bg-gray-400',
    system:       'bg-emerald-500',
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="font-medium text-sm">Order History</div>
        <div className="text-xs text-gray-500">{history.length} event{history.length === 1 ? '' : 's'}</div>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {chips.map((c) => {
          const n = counts[c.key] || 0;
          const active = filter === c.key;
          return (
            <button key={c.key} onClick={() => onFilter(c.key)}
                    className={`px-2.5 py-1 rounded-full text-xs border transition ${
                      active
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}>
              {c.label}{n ? ` (${n})` : ''}
            </button>
          );
        })}
      </div>
      {filtered.length === 0 ? (
        <p className="text-sm text-gray-500">No events to show.</p>
      ) : (
        <ul className="space-y-3">
          {filtered.map((e, i) => (
            <li key={e.id} className="relative pl-5">
              <div className={`absolute left-0 top-1.5 w-2 h-2 rounded-full ${dotByCat[e.category] || 'bg-gray-400'}`}></div>
              {i < filtered.length - 1 && <div className="absolute left-[3px] top-3.5 bottom-[-12px] w-px bg-gray-200" />}
              <div className="text-sm">
                <span className="font-medium text-gray-900">{e.title}</span>
                {e.subtitle && <span className="text-gray-500"> · {e.subtitle}</span>}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {e.actor?.name || 'system'}
                {e.ts && ` · ${format(new Date(e.ts), 'dd MMM yyyy HH:mm')}`}
              </div>
              {e.note && (
                <div className="text-xs text-gray-700 mt-1 italic whitespace-pre-wrap">"{e.note}"</div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Banner shown on OrderDetail when an Ops user has proposed a shipping
// address correction (orders.pending_address_correction is set). Shows the
// before/after diff. Admin can Approve (writes proposed values to the
// canonical shipping_* columns) or Reject (clears the proposal, hands back
// to Ops). Non-admin viewers see a read-only view.
function PendingAddressBanner({ order, isAdmin, onResolved }) {
  const [busy, setBusy] = useState(false);
  const p = order.pending_address_correction || {};

  const rows = [
    ['Customer name', order.customer_name, p.customer_name],
    ['Address 1',     order.shipping_address1, p.address1],
    ['Address 2',     order.shipping_address2, p.address2],
    ['Landmark',      order.landmark, p.landmark],
    ['City',          order.shipping_city, p.city],
    ['State / Zone',  order.shipping_zone, p.zone],
    ['Postcode',      order.shipping_postcode, p.postcode],
    ['Country',       order.shipping_country, p.country],
    ['Phone',         order.customer_phone, p.customer_phone],
  ];
  const changed = (cur, prop) =>
    prop !== undefined && prop !== null && String(prop).trim() !== '' && String(prop) !== String(cur || '');

  async function approve() {
    if (!confirm('Apply this address update to the order?')) return;
    setBusy(true);
    try {
      await adminAddressApi.approve(order.id);
      toast.success('Address updated');
      onResolved?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Approve failed');
    } finally { setBusy(false); }
  }

  async function reject() {
    const note = window.prompt('Reason for rejecting this address correction?', '');
    if (note === null) return;
    setBusy(true);
    try {
      await adminAddressApi.reject(order.id, { note });
      toast.success('Address correction rejected; order returned to Ops');
      onResolved?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Reject failed');
    } finally { setBusy(false); }
  }

  return (
    <div className="mt-4 p-3 bg-amber-50 border border-amber-300 rounded-lg">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="text-sm font-semibold text-amber-900">
          Pending address correction · waiting for admin review
        </div>
        {isAdmin && (
          <div className="flex gap-2 shrink-0">
            <button onClick={approve} disabled={busy}
                    className="text-xs px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50">
              Approve
            </button>
            <button onClick={reject} disabled={busy}
                    className="text-xs px-3 py-1 bg-white border border-red-300 text-red-700 rounded hover:bg-red-100 disabled:opacity-50">
              Reject
            </button>
          </div>
        )}
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-amber-900">
            <th className="font-medium pr-2 py-1">Field</th>
            <th className="font-medium pr-2 py-1">Current</th>
            <th className="font-medium pr-2 py-1">Proposed</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, cur, prop]) => (
            <tr key={label} className={changed(cur, prop) ? 'bg-amber-100' : ''}>
              <td className="pr-2 py-0.5 text-gray-700">{label}</td>
              <td className="pr-2 py-0.5 text-gray-900">{cur || '—'}</td>
              <td className="pr-2 py-0.5 text-gray-900 font-medium">{prop ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {p.submitted_note && (
        <div className="mt-2 text-xs text-amber-900 italic">Ops note: "{p.submitted_note}"</div>
      )}
    </div>
  );
}
